import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, DollarSign, FileText, Briefcase, TrendingUp, Users, 
  Clock, AlertTriangle, RefreshCw, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#F97316', '#3B82F6', '#22C55E', '#EAB308', '#EF4444', '#8B5CF6', '#EC4899'];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount || 0);
};

export const ReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState('monthly');
  
  // Report data
  const [financialReport, setFinancialReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [pipelineReport, setPipelineReport] = useState(null);
  const [contractorReport, setContractorReport] = useState(null);
  const [marginReport, setMarginReport] = useState(null);
  const [agingReport, setAgingReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [year, period]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [financial, revenue, pipeline, contractor, margin, aging] = await Promise.all([
        axios.get(`${API}/api/reports/financial`, { headers }),
        axios.get(`${API}/api/reports/revenue?period=${period}&year=${year}`, { headers }),
        axios.get(`${API}/api/reports/pipeline`, { headers }),
        axios.get(`${API}/api/reports/contractor-performance`, { headers }),
        axios.get(`${API}/api/reports/profit-margins`, { headers }),
        axios.get(`${API}/api/reports/invoice-aging`, { headers }),
      ]);
      
      setFinancialReport(financial.data);
      setRevenueReport(revenue.data);
      setPipelineReport(pipeline.data);
      setContractorReport(contractor.data);
      setMarginReport(margin.data);
      setAgingReport(aging.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const pipelineChartData = pipelineReport ? Object.entries(pipelineReport.pipeline).map(([status, data]) => ({
    name: status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1),
    count: data.count,
    value: data.value
  })).filter(d => d.count > 0) : [];

  const agingChartData = agingReport ? Object.entries(agingReport.aging).map(([bucket, data]) => ({
    name: bucket === 'current' ? 'Not Due' : bucket + ' days',
    count: data.count,
    total: data.total
  })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports & Analytics</h1>
          <p className="text-muted-foreground">Business insights and performance metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchReports}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-green-500">{formatCurrency(revenueReport?.total_revenue)}</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold text-blue-500">{formatCurrency(pipelineReport?.total_pipeline_value)}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-full">
                <Briefcase className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold text-yellow-500">{formatCurrency(agingReport?.total_outstanding)}</p>
              </div>
              <div className="p-3 bg-yellow-500/20 rounded-full">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Margin</p>
                <p className="text-2xl font-bold text-primary">{marginReport?.average_margin_percent || 0}%</p>
              </div>
              <div className="p-3 bg-primary/20 rounded-full">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="contractors">Contractors</TabsTrigger>
          <TabsTrigger value="margins">Profit Margins</TabsTrigger>
          <TabsTrigger value="aging">Invoice Aging</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Chart */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" /> Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={revenueReport?.data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="period" stroke="#888" />
                    <YAxis stroke="#888" tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                    />
                    <Bar dataKey="revenue" fill="#F97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Pipeline Pie */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-primary" /> Jobs Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pipelineChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="count"
                      label={({ name, count }) => `${name}: ${count}`}
                    >
                      {pipelineChartData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Financial Summary */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" /> Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Quoted</p>
                  <p className="text-xl font-bold">{formatCurrency(financialReport?.total_quoted)}</p>
                  <p className="text-xs text-muted-foreground">{financialReport?.quote_count || 0} quotes</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Invoiced</p>
                  <p className="text-xl font-bold">{formatCurrency(financialReport?.total_invoiced)}</p>
                  <p className="text-xs text-muted-foreground">{financialReport?.invoice_count || 0} invoices</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-xl font-bold text-green-500">{formatCurrency(financialReport?.total_paid)}</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Purchase Orders</p>
                  <p className="text-xl font-bold">{formatCurrency(financialReport?.total_purchase_orders)}</p>
                  <p className="text-xs text-muted-foreground">{financialReport?.po_count || 0} POs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Revenue by {period}</CardTitle>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={revenueReport?.data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="period" stroke="#888" />
                  <YAxis stroke="#888" tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  />
                  <Bar dataKey="revenue" fill="#22C55E" radius={[4, 4, 0, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Jobs Pipeline ({pipelineReport?.total_jobs || 0} jobs)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                {Object.entries(pipelineReport?.pipeline || {}).map(([status, data]) => (
                  <div key={status} className="p-4 bg-secondary/30 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground capitalize">{status.replace('_', ' ')}</p>
                    <p className="text-2xl font-bold">{data.count}</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(data.value)}</p>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pipelineChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" stroke="#888" />
                  <YAxis type="category" dataKey="name" stroke="#888" width={100} />
                  <Tooltip 
                    formatter={(value, name) => name === 'value' ? formatCurrency(value) : value}
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  />
                  <Bar dataKey="count" fill="#3B82F6" name="Jobs" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contractors Tab */}
        <TabsContent value="contractors">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" /> Contractor Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Contractor</TableHead>
                    <TableHead>Trades</TableHead>
                    <TableHead className="text-right">Total POs</TableHead>
                    <TableHead className="text-right">PO Value</TableHead>
                    <TableHead className="text-right">Completed</TableHead>
                    <TableHead className="text-right">Completion Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(contractorReport?.performance || []).map((c) => (
                    <TableRow key={c.contractor_id} className="border-border">
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {(c.trade_types || []).slice(0, 2).map((t) => (
                            <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{c.total_pos}</TableCell>
                      <TableCell className="text-right">{formatCurrency(c.total_po_value)}</TableCell>
                      <TableCell className="text-right">{c.completed_pos}</TableCell>
                      <TableCell className="text-right">
                        <span className={c.completion_rate >= 80 ? 'text-green-500' : c.completion_rate >= 50 ? 'text-yellow-500' : 'text-red-500'}>
                          {c.completion_rate}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Margins Tab */}
        <TabsContent value="margins">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Profit Margins by Job</CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <span>Total Profit: <strong className="text-green-500">{formatCurrency(marginReport?.total_profit)}</strong></span>
                  <span>Avg Margin: <strong className="text-primary">{marginReport?.average_margin_percent}%</strong></span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Job</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Costs</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(marginReport?.jobs || []).slice(0, 20).map((job) => (
                    <TableRow key={job.job_id} className="border-border">
                      <TableCell className="font-medium">{job.title}</TableCell>
                      <TableCell className="text-muted-foreground">{job.client_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(job.revenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(job.costs)}</TableCell>
                      <TableCell className={`text-right font-medium ${job.profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {formatCurrency(job.profit)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className={job.margin_percent >= 20 ? 'bg-green-500/20 text-green-400' : job.margin_percent >= 10 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}>
                          {job.margin_percent}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aging Tab */}
        <TabsContent value="aging">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" /> Invoice Aging Report
                </CardTitle>
                <span className="text-lg font-bold">Total Outstanding: {formatCurrency(agingReport?.total_outstanding)}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 mb-6">
                {Object.entries(agingReport?.aging || {}).map(([bucket, data]) => (
                  <div key={bucket} className={`p-4 rounded-lg text-center ${
                    bucket === 'current' ? 'bg-green-500/20' :
                    bucket === '1-30' ? 'bg-yellow-500/20' :
                    bucket === '31-60' ? 'bg-orange-500/20' :
                    'bg-red-500/20'
                  }`}>
                    <p className="text-sm text-muted-foreground">{bucket === 'current' ? 'Not Due' : bucket + ' days'}</p>
                    <p className="text-2xl font-bold">{data.count}</p>
                    <p className="text-sm font-medium">{formatCurrency(data.total)}</p>
                  </div>
                ))}
              </div>
              
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={agingChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#888" />
                  <YAxis stroke="#888" tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                  />
                  <Bar dataKey="total" fill="#EAB308" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
